import utils from "./utils.js";
import { node, transition } from "./styles";

export class Rectangle {
	constructor(
		xPos,
		yPos,
		width,
		height,
		fillColor,
		strokeColor,
		strokeWidth
	) {
		this.x = xPos;
		this.y = yPos;

		this.width = width;
		this.height = height;

		this.fillColor = fillColor;
		this.strokeColor = strokeColor;
		this.strokeWidth = strokeWidth;
	}

	Draw(context) {
		context.beginPath();
		context.lineWidth = this.strokeWidth;
		context.strokeStyle = this.strokeColor;
		context.fillStyle = this.fillColor;

		context.rect(
			this.x - this.width / 2,
			this.y - this.height / 2,
			this.width,
			this.height
		);

		context.stroke();
		context.fill();
	}
}

export class Circle {
	constructor(xPos, yPos, radius, fillColor, strokeColor, strokeWidth) {
		this.x = xPos;
		this.y = yPos;
		this.radius = radius;
		this.fillColor = fillColor;
		this.strokeColor = strokeColor;
		this.strokeWidth = strokeWidth;
	}

	Draw(context) {
		context.beginPath();
		context.arc(this.x, this.y, this.radius, 0, 2 * Math.PI);
		context.lineWidth = this.strokeWidth;
		context.strokeStyle = this.strokeColor;
		context.stroke();
		context.fillStyle = this.fillColor;
		context.fill();
		context.closePath();
	}
}

export class Handle {
	constructor(x, y, symbols, strokeColor) {
		this.x = x;
		this.y = y;

		this.strokeColor = strokeColor;
		this.symbols = symbols;
		this.symbolsText = null;

		this.rect = null;
		this.leftCircle = null;
		this.rightCircle = null;
	}

	Draw(context) {
		if (!this.symbols || !this.symbols.length > 0) return;

		this.symbolsText = this.symbols.join(", ");
		const symbolsLength =
			context.measureText(this.symbolsText).width + transition.padding;

		this.leftCircle = new Circle(
			this.x - symbolsLength / 2,
			this.y,
			transition.controlPointHeight / 2,
			transition.controlPointFill,
			this.strokeColor,
			2
		);
		this.rightCircle = new Circle(
			this.x + symbolsLength / 2,
			this.y,
			transition.controlPointHeight / 2,
			transition.controlPointFill,
			this.strokeColor,
			2
		);

		this.leftCircle.Draw(context);
		this.rightCircle.Draw(context);

		this.rect = new Rectangle(
			this.x,
			this.y,
			symbolsLength,
			transition.controlPointHeight,
			transition.controlPointFill,
			"transparent",
			2
		);

		context.beginPath();
		context.strokeStyle = this.strokeColor;
		context.lineWidth = 1;
		context.moveTo(
			this.x - symbolsLength / 2,
			this.y - transition.controlPointHeight / 2 - 0.5
		);
		context.lineTo(
			this.x + symbolsLength / 2,
			this.y - transition.controlPointHeight / 2 - 0.5
		);

		context.moveTo(
			this.x - symbolsLength / 2,
			this.y + transition.controlPointHeight / 2 + 0.5
		);
		context.lineTo(
			this.x + symbolsLength / 2,
			this.y + transition.controlPointHeight / 2 + 0.5
		);
		context.stroke();
		context.closePath();

		this.rect.Draw(context);
		this.DrawSymbols(context);
	}

	DrawSymbols(context) {
		context.font = node.font;
		context.textAlign = "center";
		context.fillStyle = transition.textColor;
		context.fillText(this.symbolsText, this.x, this.y + 5);
	}
}

export class Node {
	constructor(xPos, yPos, id, name, initial, accept) {
		this.x = xPos;
		this.y = yPos;
		this.id = id;
		this.name = name;

		this.initial = initial;
		this.accept = accept;

		this.outerCircle = null;
		this.innerCircle = null;

		this.strokeColor = node.stroke;
	}

	Draw(context, clicked) {
		//SETTING UP COLORS
		this.strokeColor = node.stroke;
		if (clicked) {
			this.strokeColor = node.clickedStroke;
		}

		// DRAWING INITIAL ARROW
		if (this.initial) {
			this.DrawInitialArrow(context);
		}

		// DRAWING OUTER CIRCLE
		this.DrawOuterCircle(context);

		// DRAWING INNER CIRCLE
		if (this.accept) {
			this.DrawInnerCircle(context);
		}

		//DRAWING TEXT
		context.font = node.font;
		context.textAlign = "center";
		context.fillStyle = node.textColor;
		context.fillText(this.name, this.x, this.y + 4);
	}

	DrawInitialArrow(context) {
		context.beginPath();
		context.moveTo(
			this.x - node.outerRadius * 2,
			this.y - node.outerRadius * 2
		);
		context.lineTo(this.x, this.y);
		context.strokeStyle = this.strokeColor;
		context.lineWidth = transition.lineWidth;
		context.stroke();
		context.closePath();

		const x = this.x + node.outerRadius * Math.cos(Math.PI * (5 / 4)) - 5;
		const y = this.y + node.outerRadius * Math.sin(Math.PI * (5 / 4)) - 5;

		let arrow = new Arrow(x, y, Math.PI / 4, 5, 5, this.strokeColor);
		arrow.Draw(context);
	}
	DrawOuterCircle(context) {
		if (this.outerCircle == undefined) {
			this.outerCircle = new Circle(
				this.x,
				this.y,
				node.outerRadius,
				node.fill,
				this.strokeColor,
				node.strokeWidth
			);
		} else {
			this.outerCircle.x = this.x;
			this.outerCircle.y = this.y;
		}

		this.outerCircle.strokeColor = this.strokeColor;
		this.outerCircle.Draw(context);
	}
	DrawInnerCircle(context) {
		if (this.innerCircle == undefined) {
			this.innerCircle = new Circle(
				this.x,
				this.y,
				node.innerRadius,
				node.fill,
				this.strokeColor,
				node.strokeWidth
			);
		} else {
			this.innerCircle.x = this.x;
			this.innerCircle.y = this.y;
		}

		this.innerCircle.strokeColor = this.strokeColor;
		this.innerCircle.Draw(context);
	}
}

export class TransitionLine {
	constructor(
		id,
		startNodeID,
		endNodeID,
		startXPos,
		startYPos,
		endXPos,
		endYPos,
		controlPointX,
		controlPointY,
		symbols,
		isPlaceholder
	) {
		this.id = id;
		this.startNodeID = startNodeID;
		this.endNodeID = endNodeID;

		this.startXPos = startXPos;
		this.startYPos = startYPos;
		this.endXPos = endXPos;
		this.endYPos = endYPos;
		this.controlPointX = controlPointX;
		this.controlPointY = controlPointY;

		this.handle = null;
		this.symbols = symbols;
		this.isPlaceholder = isPlaceholder;
	}

	Draw(context, clicked) {
		const sameState =
			this.startXPos === this.endXPos && this.startYPos === this.endYPos;

		//#region RECALCULATE THE END POINT DEPENDING ON NODE RADIUS
		const [dx, dy] = utils.vector(
			this.endXPos,
			this.endYPos,
			this.controlPointX,
			this.controlPointY
		);
		const distance = utils.distanceXY(
			this.endXPos,
			this.endYPos,
			this.controlPointX,
			this.controlPointY
		);
		const dxn = dx / distance;
		const dyn = dy / distance;

		let endX =
			this.endXPos - dxn * (node.outerRadius + transition.arrowLength);
		let endY =
			this.endYPos - dyn * (node.outerRadius + transition.arrowLength);
		let startX = this.startXPos;
		let startY = this.startYPos;

		if (sameState) endX -= node.innerRadius / 2;
		if (sameState) startX += node.innerRadius / 2;
		//#endregion

		//#region CHOOSING COLORS
		let lineColor = transition.lineColor;
		if (clicked) {
			lineColor = transition.clickedLineColor;
		} else if (this.isPlaceholder) {
			lineColor = transition.placeholderLineColor;
		}

		let handleStrokeColor = transition.controlPointStroke;
		if (clicked) {
			handleStrokeColor = transition.clickedControlPointStroke;
		}

		let arrowColor = transition.arrowColor;
		if (clicked) {
			arrowColor = transition.clickedArrowColor;
		} else if (this.isPlaceholder) {
			arrowColor = transition.placeholderArrowColor;
		}

		//#endregion

		//#region DRAWING A CURVE LINE
		context.beginPath();
		context.moveTo(startX, startY);

		if (sameState) {
			// The transition starts and ends in the same node
			// Rendered differently for aesthetic reasons
			context.quadraticCurveTo(
				this.controlPointX,
				this.controlPointY,
				endX,
				endY
			);
		} else {
			context.bezierCurveTo(
				this.controlPointX,
				this.controlPointY,
				this.controlPointX,
				this.controlPointY,
				endX,
				endY
			);
		}
		context.lineWidth = 2;
		context.strokeStyle = lineColor;
		context.stroke();
		context.closePath();

		//#endregion

		//#region DRAWING ARROW
		let arrow = new Arrow(
			endX,
			endY,
			Math.atan2(dy, dx),
			transition.arrowLength,
			transition.arrowWidth,
			arrowColor
		);
		arrow.Draw(context);
		//#endregion

		//#region CONTROL POINT SET UP + HANDLE
		this.handle = new Handle(
			this.controlPointX,
			this.controlPointY,
			this.symbols,
			handleStrokeColor
		);
		this.handle.Draw(context);
		//#endregion
	}
}

export class Arrow {
	constructor(xPos, yPos, angle, length, width, color) {
		this.x = xPos;
		this.y = yPos;

		this.angle = angle;
		this.length = length;
		this.width = width;
		this.color = color;
	}

	Draw(context) {
		let v1x, v1y, v2x, v2y, v3x, v3y;

		v1x = this.x + Math.cos(this.angle - Math.PI / 2) * (this.width / 2);
		v1y = this.y + Math.sin(this.angle - Math.PI / 2) * (this.width / 2);

		v2x = this.x + Math.cos(this.angle) * this.length;
		v2y = this.y + Math.sin(this.angle) * this.length;

		v3x = this.x + Math.cos(Math.PI / 2 + this.angle) * (this.width / 2);
		v3y = this.y + Math.sin(Math.PI / 2 + this.angle) * (this.width / 2);

		context.beginPath();
		context.moveTo(this.x, this.y);
		context.lineTo(v1x, v1y);
		context.lineTo(v2x, v2y);
		context.lineTo(v3x, v3y);
		context.lineTo(this.x, this.y);

		context.lineWidth = 2;
		context.strokeStyle = this.color;
		context.fillStyle = this.color;
		context.fill();
		context.stroke();
		context.closePath();
	}
}

// NO ANGLE
// context.moveTo(this.x - this.length / 2, this.y - this.width / 2);
// context.lineTo(this.x - this.length / 2, this.y + this.width);
// context.lineTo(this.x + this.length / 2, this.y);
// context.lineTo(this.x - this.length / 2, this.y - this.width / 2);
